// src/MapScreen.js
import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { supabase } from './lib/supabase';
import { useT } from './lib/i18n';

// ── Couleurs & icônes par type ────────────────────────────────────────────────
const TC = {
  ville:  '#7B9FD4',
  nature: '#6DBF8A',
  ile:    '#C47BAA',
  cote:   '#7BC4C4',
  event:  '#F4A928',
};
const TE = {
  ville:  '🏙',
  nature: '🌿',
  ile:    '🏝',
  cote:   '🌊',
  event:  '🎭',
};
// Icônes par sous-catégorie d'event
const EVENT_ICONS = {
  sport:        '🏆',
  music:        '🎵',
  culture:      '🎭',
  nature_event: '🌸',
};
// SVG icons for map markers — formes universellement lisibles à petite taille
const MARKER_SVG = {
  // Maison : toit triangulaire + façade + porte
  ville:  `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1.5 7 L7 2 L12.5 7"/><rect x="2.5" y="7" width="9" height="6" rx="0.5"/><rect x="5.5" y="9.5" width="3" height="3.5" rx="0.5"/></svg>`,
  // Arbre : couronne ronde + tronc
  nature: `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="7" cy="5.5" r="3.5"/><line x1="7" y1="9" x2="7" y2="13"/></svg>`,
  // Palmier : tronc courbe + deux palmes
  ile:    `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M6.5 13 C7 10 9 8 8.5 6"/><path d="M8.5 6 Q5.5 2.5 3 5"/><path d="M8.5 6 Q11.5 3 12 6"/><path d="M8.5 6 Q9 2 7.5 1"/></svg>`,
  // Deux vagues (inchangé)
  cote:   `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M0.5 5.5 C2.5 3.5 4.5 7.5 7 5.5 S11 3.5 13.5 5.5"/><path d="M0.5 9 C2.5 7 4.5 11 7 9 S11 7 13.5 9"/></svg>`,
  // Calendrier (inchangé)
  event:  `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="1.5" y="3" width="11" height="10" rx="1.5"/><line x1="4.5" y1="1" x2="4.5" y2="5"/><line x1="9.5" y1="1" x2="9.5" y2="5"/><line x1="1.5" y1="6.5" x2="12.5" y2="6.5"/><circle cx="7" cy="9.5" r="1.2" fill="currentColor" stroke="none"/></svg>`,
};

const CONTINENTS = [
  { key:'contAmerique', lat:15,  lng:-80,  zoom:3 },
  { key:'contEurope',   lat:50,  lng:15,   zoom:4 },
  { key:'contAfrique',  lat:5,   lng:20,   zoom:3 },
  { key:'contMOrient',  lat:25,  lng:45,   zoom:4 },
  { key:'contAsie',     lat:30,  lng:100,  zoom:3 },
  { key:'contOceanie',  lat:-25, lng:135,  zoom:4 },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function nextOccurrence(iso, recurrent) {
  if (!iso || !recurrent) return iso;
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(iso + 'T12:00:00');
  while (d < today) d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().split('T')[0];
}

function fmtDate(iso, locale = 'fr-FR') {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString(locale, { day:'numeric', month:'short', year:'numeric' });
}

function daysUntil(iso) {
  if (!iso) return null;
  const diff = new Date(iso + 'T12:00:00') - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

// ── Clustering ────────────────────────────────────────────────────────────────
function cluster(dests, zoom) {
  const g = zoom<=3?22 : zoom<=4?10 : zoom<=5?5 : zoom<=6?2.5 : zoom<=7?1.2 : 0;

  if (g === 0) return dests.map(d => ({ k:'s', d }));

  const cells = {};
  dests.forEach(d => {
    const key = `${Math.floor(d.lng/g)}_${Math.floor(d.lat/g)}`;
    (cells[key] = cells[key] || []).push(d);
  });
  return Object.values(cells).map(group => {
    if (group.length === 1) return { k:'s', d:group[0] };
    const lat = group.reduce((s,d) => s+d.lat, 0) / group.length;
    const lng = group.reduce((s,d) => s+d.lng, 0) / group.length;
    const tc = {};
    group.forEach(d => tc[d.type] = (tc[d.type]||0)+1);
    const dt = Object.entries(tc).sort((a,b) => b[1]-a[1])[0][0];
    return { k:'c', lat, lng, count:group.length, dt };
  });
}

// ── Composant ─────────────────────────────────────────────────────────────────
// Returns the localized field value for a destination row, falling back to the base field.
function ln(dest, field, lang) {
  if (!dest) return '';
  if (!lang || lang === 'fr') return dest[field] || '';
  return dest[`${field}_${lang}`] || dest[field] || '';
}

const PLAN_ROTS = [-4, 3, -2, 5, -3, 4, -5, 2, -1, 3];
function planRotFor(id) {
  let h = 0;
  const s = String(id);
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) & 0xffff;
  return PLAN_ROTS[h % PLAN_ROTS.length];
}

export default function MapScreen({ selIds = [], setSelIds, onBack, onGoToDest, appLang, destPhotos = {}, typePhotos = {}, readOnly = false, onSignup, voyeurTitle, planPhotos = {}, initialFilter = null }) {
  const t = useT(appLang);
  const dateLocale = appLang === 'en' ? 'en-GB' : appLang === 'es' ? 'es-ES' : appLang === 'zh' ? 'zh-CN' : 'fr-FR';
  const mapRef      = useRef(null);
  const mapInstance = useRef(null);
  const layerRef    = useRef(null);
  const destsRef    = useRef([]);
  const addedRef    = useRef(new Set());

  const initF = readOnly ? 'sel' : (initialFilter || null);
  const mapFilterRef   = useRef(initF);
  const planPhotosRef  = useRef(planPhotos);

  const [loading,    setLoading]    = useState(true);
  const [status,     setStatus]     = useState('');
  const [detail,     setDetail]     = useState(null);
  const [detailPhoto, setDetailPhoto] = useState(null);
  const [savedView,  setSavedView]  = useState(null);
  const [toast,      setToast]      = useState(false);
  const [activeCont, setActiveCont] = useState(null);
  const [addedCount, setAddedCount] = useState(0);
  const [mapFilter,  setMapFilter]  = useState(initF);

  // ── Photo Wikidata pour le detail ────────────────────────────────────────
  useEffect(() => {
    if (!detail) { setDetailPhoto(null); return; }
    const BAD = /flag|coat|logo|blason|emblem|icon|seal|arms|wappen|shield|banner|locator|map|carte|plan\b/i;
    const base = destPhotos[detail.name] || null;
    if (base) { setDetailPhoto(base); return; }
    // Ne pas afficher la générique immédiatement — attendre Wikidata pour éviter le flash
    setDetailPhoto(null);
    let cancelled = false;
    const tryWikidata = async (lang, title) => {
      try {
        const r = await fetch(`https://www.wikidata.org/w/api.php?action=wbgetentities&sites=${lang}wiki&titles=${encodeURIComponent(title)}&props=claims&format=json&origin=*`);
        const data = await r.json();
        const entity = Object.values(data.entities || {}).find(e => e.id && e.id !== '-1');
        if (!entity) return false;
        const p18 = entity.claims?.P18?.[0]?.mainsnak?.datavalue?.value;
        if (!p18 || BAD.test(p18)) return false;
        if (!cancelled) setDetailPhoto(`https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(p18.replace(/ /g,'_'))}?width=800`);
        return true;
      } catch { return false; }
    };
    const WIKIDATA_EN_OVERRIDES = { 'Grenade': 'Granada' };
    const en = WIKIDATA_EN_OVERRIDES[detail.name] || detail.name_en || detail.name;
    tryWikidata('en', en).then(ok => ok || tryWikidata('fr', detail.name).then(ok2 => {
      if (!ok2 && !cancelled) setDetailPhoto(typePhotos[detail.type] || typePhotos.ville || null);
    }));
    return () => { cancelled = true; };
  }, [detail]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── renderMarkers ─────────────────────────────────────────────────────────
  function renderMarkers() {
    const map   = mapInstance.current;
    const layer = layerRef.current;
    if (!map || !layer) return;

    layer.clearLayers();
    const zoom   = map.getZoom();
    const bounds = map.getBounds();
    const latBuf = (bounds.getNorth() - bounds.getSouth()) * 0.6;
    const lngBuf = (bounds.getEast()  - bounds.getWest())  * 0.6;

    const f = mapFilterRef.current;
    const vis = destsRef.current.filter(d => {
      if (d.lat < bounds.getSouth() - latBuf || d.lat > bounds.getNorth() + latBuf) return false;
      if (d.lng < bounds.getWest()  - lngBuf || d.lng > bounds.getEast()  + lngBuf) return false;
      if (!f) return true;
      if (f === 'ville')  return d.type === 'ville';
      if (f === 'nature') return ['nature', 'ile', 'cote'].includes(d.type);
      if (f === 'event')  return d.type === 'event';
      if (f === 'sel')    return addedRef.current.has(d.id);
      return true;
    });

    cluster(vis, zoom).forEach(item => {
      if (item.k === 'c') {
        const col = TC[item.dt] || '#D4AF7A';
        const s   = item.count < 5 ? 36 : item.count < 15 ? 46 : 58;
        const icon = L.divIcon({
          html: `<div style="width:${s}px;height:${s}px;border-radius:50%;background:${col}22;border:2px solid ${col}CC;display:flex;align-items:center;justify-content:center;font-size:${s<42?11:13}px;font-weight:600;color:${col};box-shadow:0 2px 14px rgba(0,0,0,0.5);">${item.count}</div>`,
          className:'', iconSize:[s,s], iconAnchor:[s/2,s/2],
        });
        const m = L.marker([item.lat, item.lng], { icon });
        m.on('click', () => map.flyTo([item.lat, item.lng], Math.min(zoom+2, 14), { duration:0.7 }));
        layer.addLayer(m);
      } else {
        const d     = item.d;
        const isEvent = d.type === 'event';
        const col   = TC[d.type] || '#D4AF7A';
        const added = addedRef.current.has(d.id);
        const svgIcon = MARKER_SVG[d.type] || MARKER_SVG.ville;

        // Même conteneur rond pour tous les types — event distingué par la couleur or
        const markerHtml = `<div style="
            width:32px;height:32px;border-radius:50%;
            background:${added ? col+'44' : col+'1A'};
            border:2px solid ${added ? col : col+'88'};
            display:flex;align-items:center;justify-content:center;
            color:${col};
            box-shadow:0 2px 12px rgba(0,0,0,0.5)${added ? `,0 0 10px ${col}55` : ''};
            cursor:pointer;
          ">${svgIcon}</div>`;

        const photoUrl = readOnly && planPhotosRef.current[d.id];
        let icon, zOff = 0;
        if (photoUrl) {
          const rot = planRotFor(d.id);
          const polaHtml = `<div style="display:flex;flex-direction:column;align-items:center;cursor:pointer;filter:drop-shadow(2px 5px 12px rgba(0,0,0,0.7));">
            <div style="transform:rotate(${rot}deg);background:white;padding:3px 3px 0;border-radius:3px;width:50px;">
              <img src="${photoUrl}" style="width:46px;height:34px;object-fit:cover;display:block;" onerror="this.style.opacity=0.2"/>
              <div style="font-family:'Caveat',cursive;font-size:12px;font-weight:700;color:#1a1a1a;text-align:center;padding:2px 1px 3px;line-height:1;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;">${d.name.length > 8 ? d.name.slice(0,8) + '…' : d.name}</div>
            </div>
            <div style="width:2px;height:9px;background:#e74c3c;"></div>
            <div style="width:9px;height:9px;border-radius:50%;background:#e74c3c;border:1.5px solid white;margin-top:-3px;"></div>
          </div>`;
          icon = L.divIcon({ html: polaHtml, className:'', iconSize:[56,72], iconAnchor:[28,72] });
          zOff = 1000;
        } else {
          icon = L.divIcon({ html: markerHtml, className:'', iconSize:[34,34], iconAnchor:[17,17] });
        }
        const m = L.marker([d.lat, d.lng], { icon, zIndexOffset: zOff });
        m.on('click', () => {
          setSavedView({ center: map.getCenter(), zoom: map.getZoom() });
          setDetail(d);
        });
        layer.addLayer(m);
      }
    });
  }

  // ── Init carte ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    const map = L.map(mapRef.current, {
      center: [30, 10], zoom: 2,
      zoomControl: false, minZoom: 2, maxZoom: 14,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      subdomains: 'abc', maxZoom: 19,
    }).addTo(map);
    layerRef.current    = L.layerGroup().addTo(map);
    mapInstance.current = map;
    setTimeout(() => map.invalidateSize(), 0);
    map.on('moveend', renderMarkers);
    map.on('zoomend', renderMarkers);
    return () => {
      map.off('moveend', renderMarkers);
      map.off('zoomend', renderMarkers);
      map.remove();
      mapInstance.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Charger depuis Supabase ─────────────────────────────────────────────────
  useEffect(() => {
    async function load() {
      setLoading(true);
      const { data, error } = await supabase
        .from('destinations')
        .select('*')
        .order('name', { ascending: true })
        .limit(2000);
      if (error) {
        setStatus(`${t('map.errorPrefix')}${error.message}`);
      } else {
        destsRef.current = data || [];
        const evCount = (data||[]).filter(d => d.type === 'event').length;
        setStatus(`${(data||[]).length - evCount} ${t('map.destN')} · ${evCount} ${t('map.statusEvents')}`);
        setTimeout(() => setStatus(''), 4000);
        renderMarkers();
      }
      setLoading(false);
    }
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Sync addedRef avec selIds ─────────────────────────────────────────────
  useEffect(() => {
    addedRef.current = new Set(selIds);
    setAddedCount(selIds.length);
    renderMarkers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selIds]);

  // ── Actions ─────────────────────────────────────────────────────────────────
  const closeDetail = () => {
    setDetail(null);
    if (savedView && mapInstance.current) {
      setTimeout(() => mapInstance.current.setView(savedView.center, savedView.zoom), 50);
    }
  };

  const addDestination = () => {
    if (!detail || selIds.includes(detail.id)) return;
    if (setSelIds) setSelIds(prev => [...prev, detail.id]);
    setToast(true);
    setTimeout(() => {
      setToast(false);
      setDetail(null);
      if (onGoToDest) onGoToDest();
    }, 800);
  };

  const toggleFilter = (f) => {
    const next = mapFilterRef.current === f ? null : f;
    mapFilterRef.current = next;
    setMapFilter(next);
    renderMarkers();
  };

  const flyTo = (c) => {
    setActiveCont(c.key);
    mapInstance.current?.flyTo([c.lat, c.lng], c.zoom, { duration:1.1 });
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  const dispCount = (() => {
    if (!mapFilter)             return destsRef.current.length;
    if (mapFilter === 'ville')  return destsRef.current.filter(d => d.type === 'ville').length;
    if (mapFilter === 'nature') return destsRef.current.filter(d => ['nature','ile','cote'].includes(d.type)).length;
    if (mapFilter === 'event')  return destsRef.current.filter(d => d.type === 'event').length;
    if (mapFilter === 'sel')    return destsRef.current.filter(d => addedRef.current.has(d.id)).length;
    return destsRef.current.length;
  })();

  const isEvent   = detail?.type === 'event';
  const detailCol = detail ? (TC[detail.type] || '#D4AF7A') : '#D4AF7A';
  const detailEm  = detail
    ? (isEvent ? (EVENT_ICONS[detail.event_category] || '🎭') : (TE[detail.type] || '📍'))
    : '';
  const tlMap  = { ville: t('map.typeVille'), nature: t('map.typeNature'), ile: t('map.typeIle'), cote: t('map.typeCote'), event: t('map.typeEvent') };
  const evtMap = { sport: t('map.evtSport'), music: t('map.evtMusic'), culture: t('map.evtCulture'), nature_event: t('map.evtNature') };
  const detailLabel = detail
    ? (isEvent ? (evtMap[detail.event_category] || t('map.unknownEvent')) : (tlMap[detail.type] || detail.type))
    : '';
  const already      = detail ? selIds.includes(detail.id) : false;
  const detailDate   = detail?.event_date ? nextOccurrence(detail.event_date, detail.recurrent) : null;
  const detailEnd    = detail?.event_end   ? nextOccurrence(detail.event_end,  detail.recurrent) : null;
  const days         = detailDate ? daysUntil(detailDate) : null;

  return (
    <div style={S.container}>
      <div ref={mapRef} style={S.map} />

      {/* Topbar */}
      <div style={S.topbar}>
        <div style={S.topbarInner}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {onBack && (
              <button onClick={onBack} style={S.backTopBtn}>{t('map.back')}</button>
            )}
            <span style={S.title}>{voyeurTitle || t('map.title')}</span>
          </div>
          <div style={S.topbarRight}>
            {loading && <div style={S.loader}><div style={S.spinner}/><span style={{fontSize:11,color:'#7ab8c8'}}>{t('map.loading')}</span></div>}
            <div style={S.countBadge}>{dispCount}</div>
            <div style={S.countLabel}>{dispCount !== 1 ? t('map.destN') : t('map.dest1')}</div>
          </div>
        </div>
      </div>

      {/* Continents */}
      <div style={S.contStrip}>
        <div style={S.contScroll}>
          {CONTINENTS.map(c => (
            <button key={c.key} onClick={() => flyTo(c)}
              style={{...S.contBtn, ...(activeCont===c.key ? S.contBtnActive : {})}}>
              <span style={{ fontSize:11 }}>{t('map.' + c.key)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Filtres carte */}
      <div style={S.filterPanel}>
        {[
          { k:'ville',  label: t('map.filterCity'),   col:'#7B9FD4' },
          { k:'nature', label: t('map.filterNature'), col:'#6DBF8A' },
          { k:'event',  label: t('map.filterEvent'),  col:'#F4A928' },
          { k:'sel',    label: t('map.filterSel'),    col:'#D4AF7A' },
        ].map(({ k, label, col }) => (
          <button key={k} onClick={() => readOnly ? onSignup?.() : toggleFilter(k)} style={{
            display:'flex', alignItems:'center',
            background: mapFilter===k ? `${col}33` : 'rgba(14,14,14,0.88)',
            backdropFilter:'blur(10px)',
            border: `1px solid ${mapFilter===k ? col : '#2A2A2A'}`,
            borderRadius:20, padding:'6px 12px',
            cursor:'pointer', color: mapFilter===k ? col : '#A0A0A0',
            fontSize:11, fontWeight:600, whiteSpace:'nowrap',
          }}>{label}</button>
        ))}
      </div>

      {/* Status */}
      {status ? <div style={S.status}>{status}</div> : null}

      {/* ── Fiche détail — bottom sheet ── */}
      {/* Backdrop */}
      {detail && (
        <div onClick={closeDetail} style={{
          position:'absolute', inset:0, zIndex:1999,
          background:'rgba(0,0,0,0.55)', backdropFilter:'blur(2px)',
        }}/>
      )}
      <div style={{
        position:'absolute', bottom:0, left:'50%', zIndex:2000,
        width:'100%', maxWidth:480,
        borderRadius:'22px 22px 0 0',
        border:'1px solid #1e3347', borderBottom:'none',
        maxHeight:'82%', display:'flex', flexDirection:'column',
        transition:'transform 0.35s cubic-bezier(.4,0,.2,1)',
        transform: detail ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(110%)',
        boxShadow:'0 -8px 40px rgba(0,0,0,0.6)',
        overflow:'hidden',
        background:'#0d1a27',
      }}>
        {detail && (() => {
          return <>
            {/* Photo background — gauche 60% */}
            {detailPhoto && (
              <img src={detailPhoto} alt={detail.name}
                onError={e => { e.target.src = typePhotos[detail.type] || typePhotos.ville; }}
                style={{
                  position:'absolute', left:0, top:0, bottom:0,
                  width:'60%', height:'100%',
                  objectFit:'cover', objectPosition:'center',
                  zIndex:0, pointerEvents:'none',
              }}/>
            )}
            {/* Dégradé photo → fond sombre — s'étend au-delà de la photo pour l'overlap texte */}
            <div style={{
              position:'absolute', inset:0, zIndex:1, pointerEvents:'none',
              background:'linear-gradient(to right, rgba(13,26,39,0.05) 0%, rgba(13,26,39,0.4) 30%, rgba(13,26,39,0.85) 50%, #0d1a27 75%)',
            }}/>

            {/* Handle + close */}
            <div style={{
              flexShrink:0, position:'relative', zIndex:2,
              padding:'10px 16px 0',
              display:'flex', alignItems:'center', justifyContent:'space-between',
            }}>
              <div style={{ flex:1 }}/>
              <div style={{ width:40, height:4, borderRadius:2, background:'rgba(255,255,255,0.18)' }}/>
              <div style={{ flex:1, display:'flex', justifyContent:'flex-end' }}>
                <button onClick={closeDetail} style={{
                  width:28, height:28, borderRadius:'50%',
                  border:'1px solid rgba(255,255,255,0.15)',
                  background:'rgba(0,0,0,0.45)', color:'#7ab8c8',
                  fontSize:16, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1,
                }}>×</button>
              </div>
            </div>

            {/* Contenu scrollable — overlap sur la zone de gradient */}
            <div style={{
              flex:1, overflowY:'auto', position:'relative', zIndex:2,
              paddingLeft:'42%', paddingRight:14, paddingTop:14, paddingBottom:14,
            }}>
              {/* Nom */}
              <div style={{ marginBottom:10 }}>
                <div style={{ fontSize:18, fontWeight:800, color:'#E6FBFF', letterSpacing:'-0.4px', lineHeight:1.15 }}>
                  {ln(detail,'name',appLang)}
                </div>
              </div>

              {/* Dates pour les events */}
              {isEvent && detailDate && (
                <div style={{
                  display:'flex', alignItems:'center', gap:8,
                  background:'rgba(244,169,40,0.08)', border:'1px solid rgba(244,169,40,0.2)',
                  borderRadius:12, padding:'8px 12px', marginBottom:14,
                }}>
                  <div style={{ flex:1 }}>
                    <div style={{fontSize:12, color:'#F4A928', fontWeight:700}}>
                      {fmtDate(detailDate, dateLocale)}
                      {detailEnd && detailEnd !== detailDate ? ` → ${fmtDate(detailEnd, dateLocale)}` : ''}
                    </div>
                    {detail.recurrent && (
                      <div style={{fontSize:10, color:'#888', marginTop:2}}>{t('map.annualEvent')}</div>
                    )}
                  </div>
                  {days !== null && (
                    <div style={{
                      background: days <= 30 ? 'rgba(244,169,40,0.85)' : 'rgba(255,255,255,0.08)',
                      borderRadius:20, padding:'3px 8px', flexShrink:0,
                      fontSize:10, fontWeight:700,
                      color: days <= 30 ? '#0E0E0E' : '#D4AF7A',
                    }}>
                      {days <= 0 ? t('map.inProgress') : days <= 30 ? t('map.inDays').replace('{n}', days) : t('map.inMonths').replace('{n}', Math.ceil(days/30))}
                    </div>
                  )}
                </div>
              )}

              {/* Description */}
              {detail.desc && (
                <div style={{ fontSize:12, color:'#7ab8c8', lineHeight:1.65 }}>
                  {detail.desc}
                </div>
              )}
            </div>

            {/* Bouton — pleine largeur de la zone noire droite (= 100% - largeur photo 60%) */}
            <div style={{
              flexShrink:0, position:'relative', zIndex:2,
              paddingLeft:'calc(60% + 14px)', paddingRight:14, paddingTop:8,
              paddingBottom:'max(20px, env(safe-area-inset-bottom, 20px))',
            }}>
              {!readOnly && (
                <button onClick={addDestination} style={{
                  width:'100%', border:'none', borderRadius:14, padding:'12px 0',
                  cursor: already ? 'default' : 'pointer',
                  fontSize:13, fontWeight:700, letterSpacing:'0.02em',
                  ...(already
                    ? { background:'rgba(39,201,122,0.1)', color:'#27C97A', border:'1px solid rgba(39,201,122,0.25)' }
                    : { background:`linear-gradient(135deg, #006F87, #009EC2)`, color:'#fff',
                        boxShadow:'0 4px 20px rgba(0,158,194,0.35)' }
                  ),
                }}>
                  {already
                    ? (isEvent ? t('map.eventAdded') : t('map.destAdded'))
                    : (isEvent ? t('map.addToEvents') : t('map.addToDests'))
                  }
                </button>
              )}
            </div>
          </>;
        })()}
      </div>

      {/* Toast */}
      <div style={{...S.toast, ...(toast ? S.toastShow : {})}}>{t('map.toast')}</div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  container:    { position:'absolute', inset:0, background:'#0E0E0E', fontFamily:"'DM Sans',sans-serif", overflow:'hidden' },
  map:          { position:'absolute', inset:0, width:'100%', height:'100%' },
  topbar:       { position:'absolute', top:0, left:0, right:0, zIndex:1000, padding:'max(14px, env(safe-area-inset-top, 14px)) 14px 0', pointerEvents:'none' },
  topbarInner:  { background:'rgba(13,26,39,0.95)', backdropFilter:'blur(14px)', border:'1px solid #1e3347', borderRadius:16, padding:'10px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', pointerEvents:'all' },
  title:        { fontFamily:"-apple-system,'SF Pro Display',sans-serif", fontSize:22, fontWeight:800, color:'#E6FBFF', letterSpacing:'-0.5px' },
  backTopBtn:   { background:'linear-gradient(135deg,#006F87,#009EC2)', border:'none', borderRadius:20, color:'#ffffff', fontSize:13, fontWeight:700, cursor:'pointer', padding:'7px 14px', lineHeight:1, whiteSpace:'nowrap', flexShrink:0, width:'auto' },
  topbarRight:  { display:'flex', flexDirection:'column', alignItems:'center', gap:0 },
  loader:       { display:'flex', alignItems:'center', gap:6 },
  spinner:      { width:13, height:13, borderRadius:'50%', border:'2px solid rgba(0,158,194,0.2)', borderTopColor:'#009EC2', animation:'spin 0.7s linear infinite' },
  countBadge:   { fontSize:18, fontWeight:800, color:'#E6FBFF', lineHeight:1 },
  countLabel:   { fontSize:10, color:'#7ab8c8', fontWeight:500, textTransform:'uppercase', letterSpacing:'0.05em' },
  contStrip:    { position:'absolute', bottom:'max(18px, env(safe-area-inset-bottom, 18px))', left:0, right:0, zIndex:1000, padding:'0 14px', pointerEvents:'none' },
  contScroll:   { display:'flex', gap:7, overflowX:'auto', padding:'4px 14px 4px 0', pointerEvents:'all', scrollbarWidth:'none' },
  contBtn:      { flexShrink:0, background:'rgba(13,26,39,0.88)', backdropFilter:'blur(10px)', border:'1px solid #1e3347', borderRadius:16, padding:'8px 14px', cursor:'pointer', color:'#7ab8c8', display:'flex', alignItems:'center' },
  contBtnActive:{ background:'rgba(0,158,194,0.15)', borderColor:'rgba(0,158,194,0.5)', color:'#4DD4F0' },
  filterPanel:  { position:'absolute', top:'calc(max(14px, env(safe-area-inset-top, 14px)) + 62px)', right:14, zIndex:1000, display:'flex', flexDirection:'column', gap:6, alignItems:'flex-end' },
  status:       { position:'absolute', bottom:20, left:'50%', transform:'translateX(-50%)', fontSize:10, color:'#777', pointerEvents:'none', whiteSpace:'nowrap', zIndex:999, background:'rgba(14,14,14,0.75)', padding:'4px 12px', borderRadius:20 },
  toast:        { position:'absolute', top:78, left:'50%', transform:'translateX(-50%) translateY(-14px)', background:'rgba(74,196,127,0.12)', border:'1px solid rgba(74,196,127,0.3)', borderRadius:30, padding:'7px 16px', fontSize:12, color:'#4AC47F', zIndex:3000, opacity:0, transition:'all 0.3s', pointerEvents:'none', whiteSpace:'nowrap' },
  toastShow:    { opacity:1, transform:'translateX(-50%) translateY(0)' },
};
